/**
 * features/finance/Statements.tsx
 * ------------------------------------------------------------------
 * P4 — Combined family statements. One statement per household
 * (grouped by familyId): every child's invoices and the payments
 * against them, a single combined balance, a chronological ledger, and
 * arrears aging — addressed to the family's primary contact. This is
 * where the Families foundation pays off: siblings bill separately but
 * settle as one account.
 *
 * Browse households (outstanding first), open one, and print an A4
 * statement PDF in the payslip house style.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { FileText, Search, X, FileDown, ArrowLeft, Users, Mail } from 'lucide-react';
import { buildStatementPdf } from '@/lib/statementPdf';
import { AccentButton, GradientHero, SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';

import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { deliverStatement } from '@/lib/statementDelivery';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  buildFamilyStatement, fmtPula, fmtBalance, round2,
  type FamilyStatement,
} from '@/lib/financeCore';

export default function Statements() {
  const acad = useData();
  const data = useFinanceData();

  const [query, setQuery] = useState('');
  // Default OFF so EVERY household/child is visible (staff asked to see all
  // kids, not just debtors). Tick "Outstanding only" to chase arrears.
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);
  const [selectedKey, setSelectedKey] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const studentName = useMemo(() => {
    const m: Record<string, string> = {};
    acad.students.forEach((s) => { m[s.id] = s.fullName; });
    data.invoices.forEach((i) => { if (i.studentName && !m[i.studentId]) m[i.studentId] = i.studentName; });
    return m;
  }, [acad.students, data.invoices]);

  // Household rows for EVERY child, not just those already invoiced:
  //  • one row per family (from the Families list), plus
  //  • a single-child row for any pupil not grouped into a family,
  //  • then invoice totals are folded onto the matching household.
  // So a fully-paid family, a child with no invoices yet, and an ungrouped
  // pupil all still appear and open a statement.
  const rows = useMemo(() => {
    type HH = { key: string; familyId: string; name: string; contact: string; billed: number; paid: number; balance: number; children: Set<string> };
    const hh: Record<string, HH> = {};
    const famOfStudent: Record<string, string> = {};

    // 1) a household per family
    acad.families.forEach((f) => {
      hh[f.id] = { key: f.id, familyId: f.id, name: f.name ?? 'Family', contact: f.primaryContact?.name ?? '', billed: 0, paid: 0, balance: 0, children: new Set(f.studentIds ?? []) };
      (f.studentIds ?? []).forEach((sid) => { famOfStudent[sid] = f.id; });
    });
    // 2) a single-child household for every ungrouped pupil
    acad.students.forEach((s) => {
      if (s.status === 'pending' || famOfStudent[s.id]) return;
      const key = `student:${s.id}`;
      if (!hh[key]) hh[key] = { key, familyId: '', name: s.fullName, contact: s.parentContact?.guardianName ?? '', billed: 0, paid: 0, balance: 0, children: new Set([s.id]) };
    });
    // 3) fold invoice totals onto the right household
    for (const i of data.invoices) {
      if (i.status === 'void') continue;
      const key = (i.familyId && hh[i.familyId]) ? i.familyId
        : famOfStudent[i.studentId] ? famOfStudent[i.studentId]
        : `student:${i.studentId}`;
      const r = hh[key] ?? (hh[key] = { key, familyId: i.familyId ?? '', name: i.studentName ?? 'Unlinked', contact: '', billed: 0, paid: 0, balance: 0, children: new Set() });
      r.billed = round2(r.billed + i.total);
      r.paid = round2(r.paid + i.paid);
      r.balance = round2(r.balance + i.balance);
      r.children.add(i.studentId);
    }

    return Object.values(hh).map((r) => {
      const childNames = [...r.children].map((id) => studentName[id] ?? '').join(' ');
      return {
        key: r.key,
        familyId: r.familyId,
        billed: r.billed, paid: r.paid, balance: r.balance,
        childCount: r.children.size,
        name: r.name,
        contact: r.contact,
        search: `${r.name} ${r.contact} ${childNames}`.toLowerCase(),
      };
    });
  }, [acad.families, acad.students, data.invoices, studentName]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) => !onlyOutstanding || r.balance > 0)
      .filter((r) => !q || r.search.includes(q))
      .sort((a, b) => b.balance - a.balance || a.name.localeCompare(b.name));
  }, [rows, query, onlyOutstanding]);

  const statement: FamilyStatement | null = useMemo(() => {
    if (!selectedKey) return null;
    // Single ungrouped pupil (key = "student:<id>") vs a real family.
    if (selectedKey.startsWith('student:')) {
      const sid = selectedKey.slice('student:'.length);
      const stu = acad.studentById(sid);
      return buildFamilyStatement({
        familyId: selectedKey,
        familyName: stu?.fullName ?? studentName[sid] ?? 'Student',
        contactName: stu?.parentContact?.guardianName,
        contactEmail: stu?.parentContact?.email,
        members: [{ studentId: sid, name: stu?.fullName ?? studentName[sid] ?? '—' }],
        invoices: data.invoices, payments: data.payments,
        adjustments: data.adjustments, settings: data.settings,
      });
    }
    const fam = acad.familyById(selectedKey);
    const memberIds = fam?.studentIds?.length
      ? fam.studentIds
      : [...new Set(data.invoices.filter((i) => i.familyId === selectedKey).map((i) => i.studentId))];
    return buildFamilyStatement({
      familyId: selectedKey,
      familyName: fam?.name ?? 'Family',
      contactName: fam?.primaryContact?.name,
      contactEmail: fam?.primaryContact?.email,
      members: memberIds.map((id) => ({ studentId: id, name: studentName[id] ?? '—' })),
      invoices: data.invoices, payments: data.payments,
      adjustments: data.adjustments, settings: data.settings,
    });
  }, [selectedKey, acad.familyById, acad.studentById, data.invoices, data.payments, data.settings, studentName]);

  if (acad.loading || data.loading) {
    return (
      <div className="space-y-6">
        <GradientHero icon={FileText} title="Statements" subtitle="Combined family statements of account" />
        <div className="flex justify-center py-16"><Spinner label="Loading…" /></div>
      </div>
    );
  }

  const download = async (s: FamilyStatement) => {
    const logo = await loadLogoDataUrl();
    buildStatementPdf(s, logo).save(`statement_${s.familyName.replace(/\s+/g, '_')}.pdf`);
  };

  const emailStatement = async (s: FamilyStatement) => {
    if (emailing) return;
    const to = s.contactEmail;
    if (!to) { setSc({ show: true, status: 'error', message: 'No contact email on file for this family.' }); return; }
    setEmailing(true);
    setSc({ show: true, status: 'saving', message: `Emailing statement to ${to}…` });
    const res = await deliverStatement(s, { toEmail: to });
    setSc(res.emailed
      ? { show: true, status: 'success', message: `Statement emailed to ${to}.` }
      : { show: true, status: 'error', message: res.reason ?? 'Could not send the statement.' });
    setEmailing(false);
  };

  return (
    <div className="space-y-6">
      <GradientHero icon={FileText} title="Statements" subtitle="Combined family statements of account" />
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((x) => ({ ...x, show: false }))} />

      {statement ? (
        <>
          <button onClick={() => setSelectedKey('')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
            <ArrowLeft size={15} /> Back to households
          </button>

          <SoftCard hover={false}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{statement.familyName}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {statement.contactName || 'Primary contact not set'}{statement.contactEmail ? ` · ${statement.contactEmail}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => emailStatement(statement)}
                  disabled={emailing || !statement.contactEmail}
                  title={statement.contactEmail ? `Email statement to ${statement.contactEmail}` : 'No contact email on file'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <Mail size={15} /> {emailing ? 'Sending…' : 'Email statement'}
                </button>
                <AccentButton icon={FileDown} onClick={() => download(statement)}>Download statement</AccentButton>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <StatChip tone="blue" value={fmtPula(statement.billed)} label="Billed" />
              <StatChip tone="emerald" value={fmtPula(statement.paid)} label="Paid" />
              <StatChip tone={statement.balance > 0 ? 'red' : 'emerald'} value={fmtBalance(statement.balance)} label="Combined balance" />
            </div>
          </SoftCard>

          {/* Per-child */}
          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">By student</h3></div>
            <div className="p-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Student</th>
                  <th className="py-2 px-3 font-medium text-right">Billed</th>
                  <th className="py-2 px-3 font-medium text-right">Paid</th>
                  <th className="py-2 pl-3 font-medium text-right">Balance</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {statement.members.map((m) => (
                    <tr key={m.studentId}>
                      <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{m.name}</td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(m.billed)}</td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(m.paid)}</td>
                      <td className="py-2 pl-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtBalance(m.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SoftCard>

          {/* Itemised invoices */}
          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Invoices</h3></div>
            <div className="p-5 space-y-3">
              {statement.members.flatMap((m) =>
                m.invoices.map((inv) => (
                  <div key={inv.id} className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/40 text-sm">
                      <span className="font-medium text-gray-800 dark:text-gray-100">{inv.invoiceNo}
                        <span className="ml-2 font-normal text-xs text-gray-500">{m.name} · {inv.term} {inv.year}</span>
                      </span>
                      <span className="text-xs text-gray-400">{(inv.issuedAt || '').slice(0, 10)}</span>
                    </div>
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50 text-sm">
                      {inv.lines.map((l, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5">
                          <span className="text-gray-700 dark:text-gray-200">{l.label}</span>
                          <span className="text-gray-700 dark:text-gray-200">{fmtPula(l.amount)}</span>
                        </div>
                      ))}
                      {inv.discountTotal > 0 && (
                        <div className="flex items-center justify-between px-3 py-1.5 text-emerald-600 dark:text-emerald-400">
                          <span>Discount</span><span>−{fmtPula(inv.discountTotal)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-4 px-3 py-2 border-t border-gray-100 dark:border-gray-700 text-xs">
                      <span className="text-gray-500">Total <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(inv.total)}</span></span>
                      <span className="text-gray-500">Paid <span className="font-medium text-emerald-600 dark:text-emerald-400">{fmtPula(inv.paid)}</span></span>
                      <span className="text-gray-500">Balance <span className={`font-semibold ${inv.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>{fmtPula(inv.balance)}</span></span>
                    </div>
                  </div>
                )),
              )}
            </div>
          </SoftCard>

          {/* Ledger */}
          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Account activity</h3></div>
            <div className="p-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 px-3 font-medium">Description</th>
                  <th className="py-2 px-3 font-medium text-right">Charge</th>
                  <th className="py-2 px-3 font-medium text-right">Credit</th>
                  <th className="py-2 pl-3 font-medium text-right">Balance</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {statement.txns.map((t, idx) => (
                    <tr key={idx}>
                      <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{(t.date || '').slice(0, 10)}</td>
                      <td className="py-2 px-3 text-gray-800 dark:text-gray-100">{t.description}</td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{t.charge ? fmtPula(t.charge) : ''}</td>
                      <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400">{t.credit ? fmtPula(t.credit) : ''}</td>
                      <td className="py-2 pl-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtPula(t.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SoftCard>
        </>
      ) : (
        <SoftCard pad="p-0" hover={false}>
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[14rem]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search family, contact or child" className={glassFieldClass('py-2 pl-8 pr-8 text-sm')} />
              {query && <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"><X size={14} /></button>}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={onlyOutstanding} onChange={(e) => setOnlyOutstanding(e.target.checked)} className="rounded" /> Outstanding only
            </label>
          </div>
          <div className="px-4 pb-4 overflow-x-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No households match.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Family</th>
                  <th className="py-2 px-3 font-medium">Primary contact</th>
                  <th className="py-2 px-3 font-medium text-right">Children</th>
                  <th className="py-2 px-3 font-medium text-right">Billed</th>
                  <th className="py-2 pl-3 font-medium text-right">Balance</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {filtered.slice(0, 500).map((r) => (
                    <tr key={r.key} className="hover:bg-[var(--accent-link,#0d9488)]/5 cursor-pointer" onClick={() => setSelectedKey(r.key)}>
                      <td className="py-2 pr-3 text-gray-800 dark:text-gray-100 font-medium">{r.name}</td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.contact || '—'}</td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300"><span className="inline-flex items-center gap-1"><Users size={12} className="text-gray-400" />{r.childCount}</span></td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.billed)}</td>
                      <td className="py-2 pl-3 text-right font-semibold text-gray-800 dark:text-gray-100">{fmtPula(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {filtered.length > 500 && <p className="text-xs text-gray-400 mt-3">Showing 500 of {filtered.length}. Use search to find a specific family or child.</p>}
          </div>
        </SoftCard>
      )}
    </div>
  );
}
